<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'

import Menu from 'primevue/menu'
import Drawer from 'primevue/drawer'
import Avatar from 'primevue/avatar'
import ToggleSwitch from 'primevue/toggleswitch'
import Button from 'primevue/button'
import Breadcrumb from 'primevue/breadcrumb'
import NotificationBell from '@/components/layout/NotificationBell.vue'

import axios from '@/services/axios'
import { useUserStore } from '@/stores/user'
import { useSystemBannerStore } from '@/stores/systemBanner'
import { useBreadcrumbStore } from '@/stores/breadcrumb'
import { useI18n } from '@/i18n'
import AppSystemBanner from '@/components/ui/AppSystemBanner.vue'
import { tokenStore } from '@/services/tokenStore'

const router  = useRouter()
const route   = useRoute()
const userStore = useUserStore()
const bannerStore = useSystemBannerStore()
const { t }   = useI18n()

const breadcrumbStore = useBreadcrumbStore()

const mobileDrawerVisible = ref(false)
const menuProfileRef      = ref(null)
const activeSection       = ref(null)

// ── User info ──────────────────────────────────────────────────────────────
const user = computed(() => ({
  first_name: userStore.info?.first_name ?? '…',
  last_name:  userStore.info?.last_name  ?? '',
  avatar_url: userStore.info?.avatar_url ?? '/avatar-fallback.svg',
}))

const themeIsDark = computed({
  get: () => (userStore.settings?.preferredColorMode ?? 'light') === 'dark',
  set: (val) => userStore.setColorMode(val ? 'dark' : 'light'),
})

// ── Navigation definition ──────────────────────────────────────────────────
// Drei-Säulen-Modell: Dashboard → Threat News → Threat Intelligence → Detection → Admin
// Siehe VISION.md für die übergeordnete Logik (Beobachten → Verstehen → Erkennen).
const allSections = computed(() => [
  {
    id: 'dashboard',
    icon: 'pi pi-th-large',
    label: 'Dashboard',
    perm: null,
    prefix: '/dashboard',
    items: [
      { label: t('nav.dashboard'), icon: 'pi pi-th-large', route: '/dashboard' },
      { label: t('nav.activity'),  icon: 'pi pi-history',  route: '/dashboard/activity' },
    ],
  },
  {
    id: 'threat-news',
    icon: 'pi pi-globe',
    label: t('nav.threatNews'),
    perm: 'news.view',
    prefix: '/threat-news',
    overviewRoute: '/threat-news',
    items: [
      { label: t('nav.overview'),         icon: 'pi pi-compass',              route: '/threat-news' },
      { label: t('nav.newsFeed'),         icon: 'pi pi-history',                  route: '/threat-news/news' },
      { label: t('nav.ransomwarePosts'),  icon: 'pi pi-megaphone',            route: '/threat-news/posts' },
      { label: t('nav.affectedCompanies'), icon: 'pi pi-clone',               route: '/threat-news/victims' },
      { label: t('nav.threatMap'),        icon: 'pi pi-map',                  route: '/threat-news/map' },
    ],
  },
  {
    id: 'threat-intel',
    icon: 'pi pi-shield',
    label: t('nav.threatIntel'),
    perm: 'hackwatch_posts.view',
    prefix: '/threat-intel',
    overviewRoute: '/threat-intel',
    items: [
      { label: t('nav.overview'),       icon: 'pi pi-compass',              route: '/threat-intel' },
      { label: t('nav.mitreAttack'),    icon: 'pi pi-sitemap',              route: '/threat-intel/mitre' },
      { label: t('nav.threatActors'),   icon: 'pi pi-building',             route: '/threat-intel/actors' },
      { label: t('nav.attackerTools'),  icon: 'pi pi-wrench',               route: '/threat-intel/tools' },
      { label: t('nav.iocs'),           icon: 'pi pi-database',             route: '/threat-intel/iocs' },
    ],
  },
  {
    id: 'detection',
    icon: 'pi pi-desktop',
    label: t('nav.detection'),
    perm: 'siem_workflow.view',
    prefix: '/detection',
    overviewRoute: '/detection',
    items: [
      { label: t('nav.overview'),  icon: 'pi pi-compass', route: '/detection' },
      { label: t('nav.workflows'), icon: 'pi pi-list',    route: '/detection/workflows' },
      { label: t('nav.streams'),   icon: 'pi pi-code',    route: '/detection/streams' },
      { label: t('nav.tags'),      icon: 'pi pi-hashtag', route: '/detection/tags' },
    ],
  },
  {
    id: 'customers',
    icon: 'pi pi-briefcase',
    label: t('nav.customers'),
    perm: 'partner_customers.view',
    requiresPartner: true,
    prefix: '/partner',
    overviewRoute: '/partner/customers',
    items: [
      { label: t('nav.overview'),       icon: 'pi pi-compass',   route: '/partner/customers' },
      { label: t('nav.customerList'),   icon: 'pi pi-users',     route: '/partner/customers/list' },
    ],
  },
  {
    id: 'admin',
    icon: 'pi pi-sliders-h',
    label: t('nav.admin'),
    perm: 'admin.view',
    requiresAdmin: true,
    // Company-Admins (kein globaler Admin) dürfen die Sektion sehen, aber nur
    // die per-Item freigegebenen Einträge (Users). Overview bleibt Admin-only.
    allowPerms: ['company_users.view'],
    overviewRequiresAdmin: true,
    prefix: '/admin',
    overviewRoute: '/admin',
    items: [
      { label: t('nav.overview'),     icon: 'pi pi-compass',    route: '/admin',            requiresAdmin: true },
      { label: t('nav.users'),        icon: 'pi pi-users',      route: '/admin/users',      perm: 'company_users.view' },
      { label: t('nav.companies'),    icon: 'pi pi-building',   route: '/admin/companies',  requiresAdmin: true },
      { label: t('nav.roles'),        icon: 'pi pi-key',        route: '/admin/roles',      requiresAdmin: true },
      { label: t('nav.apiKeys'),      icon: 'pi pi-key',        route: '/admin/api-keys',   requiresAdmin: true },
      { label: t('nav.auditLogs'),    icon: 'pi pi-history',    route: '/admin/audit-logs', requiresAdmin: true },
      { label: t('nav.systemStatus'), icon: 'pi pi-heart-fill', route: '/admin/status',     requiresAdmin: true },
    ],
  },
  {
    // Persönliche Einstellungen — als echte Sektion, damit das Sub-Panel + die
    // Breadcrumb identisch zu allen anderen Bereichen funktionieren. Zusätzlicher
    // Einstieg über das Profil-Menü (Avatar) bleibt bestehen.
    id: 'settings',
    icon: 'pi pi-cog',
    label: t('profile.settings'),
    perm: null,
    prefix: '/user/settings',
    overviewRoute: '/user/settings/general',
    items: [
      { label: t('settingsNav.general'),       icon: 'pi pi-user', route: '/user/settings/general' },
      { label: t('settingsNav.security'),      icon: 'pi pi-lock', route: '/user/settings/security' },
      { label: t('settingsNav.notifications'), icon: 'pi pi-bell', route: '/user/settings/notifications' },
      { label: t('settingsNav.apiKeys'),       icon: 'pi pi-key',  route: '/user/settings/api-keys' },
    ],
  },
])

// Item-Level-Gating: requiresAdmin → nur globaler Admin; sonst perm via can().
function itemVisible(item) {
  if (item.requiresAdmin) return userStore.isAdmin
  return !item.perm || userStore.can(item.perm)
}

// Section-Level-Gating. requiresAdmin-Sektionen können via allowPerms zusätzlich
// für Nicht-Admins mit passender Permission geöffnet werden.
function sectionVisible(s) {
  if (s.requiresAdmin) {
    if (userStore.isAdmin) return true
    return Array.isArray(s.allowPerms) && s.allowPerms.some(p => userStore.can(p))
  }
  if (s.requiresPartner) return userStore.isPartner && (!s.perm || userStore.can(s.perm))
  return !s.perm || userStore.can(s.perm)
}

const visibleSections = computed(() =>
  allSections.value
    .filter(sectionVisible)
    .map(s => ({ ...s, items: s.items.filter(itemVisible) }))
    .filter(s => s.items.length > 0)
)

const activeSubSection = computed(() =>
  visibleSections.value.find(s => s.id === activeSection.value) ?? null
)

// „Einstellungen" wird NICHT im Haupt-Rail gelistet, sondern als eigener
// Zahnrad-Button im Footer (zwischen Benachrichtigungen und Profil) angezeigt.
const railSections   = computed(() => visibleSections.value.filter(s => s.id !== 'settings'))
const settingsSection = computed(() => visibleSections.value.find(s => s.id === 'settings') ?? null)

// Auto-sync panel with current route + clear dynamic breadcrumb title on navigation
watch(
  () => route.path,
  (path) => {
    breadcrumbStore.clearTitle()
    const match = allSections.value.find(s => path.startsWith(s.prefix))
    if (match) activeSection.value = match.id
  },
  { immediate: true }
)

// Einstiegsroute einer Sektion: Overview nur, wenn zugänglich — sonst erstes
// sichtbares Item (section.items ist hier bereits per itemVisible gefiltert).
function sectionEntryRoute(section) {
  if (section.overviewRequiresAdmin && !userStore.isAdmin) {
    return section.items?.[0]?.route
  }
  return section.overviewRoute ?? section.items?.[0]?.route
}

function selectSection(section) {
  activeSection.value = section.id
  // Klick auf Top-Level-Icon: zur Übersicht (oder zum ersten Sub-Item) navigieren.
  // Sub-Panel bleibt offen, Schließen explizit über das Chevron-Icon im Sub-Panel-Head.
  const target = sectionEntryRoute(section)
  if (target && route.path !== target) {
    navigate(target)
  }
}

// ── Breadcrumbs ────────────────────────────────────────────────────────────
const breadcrumbHome = { icon: 'pi pi-home', route: '/dashboard' }

const breadcrumbItems = computed(() => {
  const path = route.path
  const items = []

  const section = allSections.value.find(s => path.startsWith(s.prefix))
  if (!section) return items

  const sectionOverview = section.overviewRoute ?? section.items?.[0]?.route

  // Skip the overview item so it doesn't appear as a breadcrumb step — the section
  // label already links there. Without this, /threat-intel would match before /threat-intel/actors.
  const sub = section.items.find(i =>
    i.route !== sectionOverview && (path === i.route || path.startsWith(i.route + '/'))
  )
  const onOverview = path === sectionOverview

  if (onOverview) {
    // Standing on the section overview — show section as current (not clickable)
    items.push({ label: section.label })
  } else {
    // Deeper than overview — section header links back to overview, sofern zugänglich.
    // Company-Admins dürfen die Admin-Übersicht nicht öffnen → Label ohne Link.
    const overviewAccessible = !(section.overviewRequiresAdmin && !userStore.isAdmin)
    items.push(overviewAccessible
      ? { label: section.label, route: sectionOverview }
      : { label: section.label })

    if (sub) {
      const onDetailPage = path !== sub.route
      if (onDetailPage) {
        // Sub-page list is clickable so the user can navigate back
        items.push({ label: sub.label, route: sub.route })
        // Detail title: prefer dynamic name set by detail component, fall back to route meta
        const rawTitle = route.meta?.title
        const metaTitle = typeof rawTitle === 'function' ? rawTitle(route) : rawTitle
        const detailLabel = breadcrumbStore.dynamicTitle ?? metaTitle
        if (detailLabel) items.push({ label: detailLabel })
      } else {
        // On the sub-page list itself — show as current (not clickable)
        items.push({ label: sub.label })
      }
    } else {
      // No matching nav item (e.g. /user/settings) — fall back to meta title
      const rawTitle = route.meta?.title
      const fallbackLabel = typeof rawTitle === 'function' ? rawTitle(route) : rawTitle
      if (fallbackLabel) items.push({ label: fallbackLabel })
    }
  }

  return items
})

function navigate(path) {
  router.push(path)
  mobileDrawerVisible.value = false
}

function isActive(path) {
  if (route.path === path) return true
  if (!route.path.startsWith(path + '/')) return false
  // Only use prefix-match for detail pages (e.g. /hackwatch/posts/123).
  // If another nav item is a more specific match, prefer that one.
  const allItems = allSections.value.flatMap(s => s.items)
  return !allItems.some(
    item => item.route !== path &&
      (route.path === item.route || route.path.startsWith(item.route + '/'))
  )
}

// ── Profile popup menu ─────────────────────────────────────────────────────
const profileItems = computed(() => [
  {
    label: t('profile.profile'),
    items: [
      { type: 'toggleTheme' },
      { separator: true },
      { label: t('profile.logout'),   icon: 'pi pi-sign-out', command: logout },
    ],
  },
])

function openProfileMenu(e) { menuProfileRef.value?.toggle(e) }

async function logout() {
  try {
    await axios.post('/auth/logout')
  } catch (_) {
    // Backend-Logout-Fehler (z.B. abgelaufener Token) bricht den Logout nicht ab
  } finally {
    await tokenStore.clear() // löscht aus Keychain (Native) oder localStorage/sessionStorage (Web)
    router.push('/login')
  }
}

onMounted(async () => {
  await userStore.init(axios)
  bannerStore.fetchBanners(axios)
})
</script>

<template>
  <!-- ── Mobile header ─────────────────────────────────────── -->
  <header class="mobile-header theme-surface-0">
    <Button icon="pi pi-bars" text @click="mobileDrawerVisible = true" />
    <img height="28" src="/logo.png" alt="Logo" />
  </header>

  <!-- ── App shell ─────────────────────────────────────────── -->
  <div class="app-shell">

    <!-- Icon Rail -->
    <nav class="icon-rail theme-surface-0">
      <div class="rail-logo">
        <img src="/logo.png" alt="Logo" height="28" />
      </div>

      <div class="rail-items">
        <button
          v-for="section in railSections"
          :key="section.id"
          class="rail-btn"
          :class="{ 'rail-btn--active': activeSection === section.id }"
          v-tooltip.right="section.label"
          @click="selectSection(section)"
        >
          <i :class="section.icon" />
        </button>
      </div>

      <div class="rail-footer">
        <NotificationBell />

        <button
          v-if="settingsSection"
          class="rail-btn"
          :class="{ 'rail-btn--active': activeSection === 'settings' }"
          v-tooltip.right="settingsSection.label"
          @click="selectSection(settingsSection)"
        >
          <i class="pi pi-cog" />
        </button>

        <button
          class="rail-btn"
          v-tooltip.right="`${user.first_name} ${user.last_name}`"
          @click="openProfileMenu"
        >
          <Avatar :image="user.avatar_url" shape="circle" style="width:32px;height:32px;" />
        </button>

        <Menu ref="menuProfileRef" :model="profileItems" :popup="true">
          <template #item="{ item, props }">
            <div
              v-if="item.type === 'toggleTheme'"
              class="flex items-center justify-between px-3 py-2 gap-3"
              role="menuitem"
              tabindex="0"
              @click.stop
              @mousedown.stop
              @pointerdown.stop
              @keydown.space.prevent.stop="themeIsDark = !themeIsDark"
              @keydown.enter.prevent.stop="themeIsDark = !themeIsDark"
            >
              <span class="text-sm flex items-center gap-2">
                <i :class="themeIsDark ? 'pi pi-moon' : 'pi pi-sun'" />
                {{ themeIsDark ? t('profile.darkMode') : t('profile.lightMode') }}
              </span>
              <ToggleSwitch v-model="themeIsDark" @click.stop @mousedown.stop />
            </div>
            <a v-else class="flex items-center gap-2 px-3 py-2" v-bind="props.action">
              <i v-if="item.icon" :class="item.icon" />
              <span>{{ item.label }}</span>
            </a>
          </template>
        </Menu>
      </div>
    </nav>

    <!-- Sub-panel -->
    <transition name="sub-panel">
      <aside v-if="activeSubSection" class="sub-panel theme-surface-0">
        <div class="sub-panel-head">
          <span class="sub-panel-title">{{ activeSubSection.label }}</span>
          <button class="sub-close" @click="activeSection = null" v-tooltip.right="t('common.close')">
            <i class="pi pi-chevron-left" />
          </button>
        </div>

        <div class="sub-panel-body">
          <button
            v-for="item in activeSubSection.items"
            :key="item.route"
            class="sub-item"
            :class="{ 'sub-item--active': isActive(item.route) }"
            @click="navigate(item.route)"
          >
            <i :class="item.icon" class="sub-item-icon" />
            <span>{{ item.label }}</span>
          </button>
        </div>
      </aside>
    </transition>

    <!-- Main content -->
    <main
      class="main-content theme-surface-ground"
      :class="{ 'main-content--shifted': !!activeSubSection }"
    >
      <AppSystemBanner />
      <Breadcrumb
        v-if="breadcrumbItems.length"
        :home="breadcrumbHome"
        :model="breadcrumbItems"
        class="app-breadcrumb"
      >
        <template #item="{ item }">
          <router-link v-if="item.route" :to="item.route" class="bc-link">{{ item.label }}</router-link>
          <span v-else class="bc-current">{{ item.label }}</span>
        </template>
        <template #separator> <i class="pi pi-chevron-right bc-sep" /> </template>
      </Breadcrumb>
      <slot />
    </main>
  </div>

  <!-- ── Mobile Drawer ──────────────────────────────────────── -->
  <Drawer v-model:visible="mobileDrawerVisible" position="left" :modal="true">
    <div class="mb-4">
      <img height="28" src="/logo.png" alt="Logo" />
    </div>

    <div v-for="section in railSections" :key="section.id" class="mobile-section">
      <p class="mobile-section-label">
        <i :class="section.icon" class="mr-2" />{{ section.label }}
      </p>
      <button
        v-for="item in section.items"
        :key="item.route"
        class="mobile-item"
        :class="{ 'mobile-item--active': isActive(item.route) }"
        @click="navigate(item.route)"
      >
        <i :class="item.icon" class="mr-2" />
        <span>{{ item.label }}</span>
      </button>
    </div>

    <div class="mobile-section" style="margin-top: auto; padding-top: 1rem; border-top: 1px solid var(--p-content-border-color)">
      <button class="mobile-item" @click="navigate('/user/settings')">
        <i class="pi pi-cog mr-2" /> {{ t('profile.settings') }}
      </button>
      <button class="mobile-item" @click="logout">
        <i class="pi pi-sign-out mr-2" /> {{ t('profile.logout') }}
      </button>
    </div>
  </Drawer>
</template>

